/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * A field of a profile message.
 *
 * Instances carry their profile metadata (name, base type, scale, offset, units,
 * subfields, components) alongside the decoded values, because FIT is
 * self-describing and a decoder meets fields the profile does not know.
 */
public class Field(
    override val fieldName: String,
    override val fieldNum: UByte,
    override val baseType: BaseType,
    override val scale: Double = Fit.FIELD_DEFAULT_SCALE,
    override val offset: Double = Fit.FIELD_DEFAULT_OFFSET,
    override val units: String = "",
    public val isAccumulated: Boolean = false,
) : FieldBase() {
    public val subFields: MutableList<SubField> = mutableListOf()
    public val components: MutableList<FieldComponent> = mutableListOf()

    /** True when this field was produced by unpacking a component, not read directly. */
    public var isExpandedField: Boolean = false
        internal set

    public val hasComponents: Boolean get() = components.isNotEmpty()

    public val hasSubFields: Boolean get() = subFields.isNotEmpty()

    /** Copies metadata and values, so a decoded message can be handed out safely. */
    internal constructor(other: Field) : this(
        other.fieldName,
        other.fieldNum,
        other.baseType,
        other.scale,
        other.offset,
        other.units,
        other.isAccumulated,
    ) {
        isExpandedField = other.isExpandedField
        declaredTypeId = other.declaredTypeId
        other.subFields.forEach { subFields.add(SubField(it)) }
        other.components.forEach { components.add(FieldComponent(it)) }
        values.addAll(other.values)
    }

    public fun addSubField(subField: SubField): Field {
        subFields.add(subField)
        return this
    }

    public fun addComponent(component: FieldComponent): Field {
        components.add(component)
        return this
    }

    override fun getSubField(index: Int): SubField? = subFields.getOrNull(index)

    override fun getSubField(name: String): SubField? = subFields.firstOrNull { it.name == name }

    override fun resolveSubField(selector: SubFieldSelector, mesg: Mesg?): SubField? =
        when (selector) {
            is SubFieldSelector.MainField -> null
            is SubFieldSelector.Active -> mesg?.let { m -> subFields.firstOrNull { it.canMesgSupport(m) } }
            is SubFieldSelector.AtIndex -> getSubField(selector.index)
            is SubFieldSelector.Named -> getSubField(selector.name)
        }

    /** The name this field goes by under [selector], falling back to [fieldName]. */
    public fun nameOf(selector: SubFieldSelector, mesg: Mesg? = null): String =
        resolveSubField(selector, mesg)?.name ?: fieldName

    /** The units this field is measured in under [selector]. */
    public fun unitsOf(selector: SubFieldSelector, mesg: Mesg? = null): String =
        resolveSubField(selector, mesg)?.units ?: units

    /** All stored values, scale and offset applied, invalid entries as null. */
    public fun toList(): List<Any?> = List(numValues) { getValue(it) }
}
